import { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Image from '../../../components/AppImage';

import { X, Loader2, CheckCircle } from 'lucide-react';
import { getAllPlatforms } from '../../../constants/platformLogos';

const ConnectSocialModal = ({ isOpen, onClose, onConnect, brandId }) => {
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [credentials, setCredentials] = useState({
    username: '',
    accessToken: ''
  });
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({
    platform: '',
    accountName: '',
    accountUrl: '',
    followerCount: ''
  });

  const platforms = getAllPlatforms();
  
  // Map platforms to dropdown format with value and label
  const availableOptions = platforms?.map(platform => ({
    value: platform?.id,
    label: platform?.name,
    ...platform
  }));

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePlatformSelect = (platformId) => {
    setSelectedPlatform(platformId);
    setFormData(prev => ({
      ...prev,
      platform: platformId
    }));
    setSuccessMessage('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setSuccessMessage('');

    try {
      await onConnect({
        platform: formData?.platform,
        accountName: formData?.accountName,
        accountUrl: formData?.accountUrl,
        followerCount: parseInt(formData?.followerCount) || 0
      });

      setSuccessMessage(`${formData?.accountName} connected successfully!`);
      
      // Reset form for next connection
      setFormData({
        platform: '',
        accountName: '',
        accountUrl: '',
        followerCount: ''
      });
      setSelectedPlatform(null);
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error connecting account:', error);
      alert(error?.message || 'Failed to connect account');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedPlatform(null);
    setFormData({
      platform: '',
      accountName: '',
      accountUrl: '',
      followerCount: ''
    });
    setSuccessMessage('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-xl max-w-md w-full p-6 relative">
        <div className="flex justify-between items-center border-b pb-4 mb-4">
          <h2 className="text-2xl font-bold text-foreground">Connect Social Platform</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm font-medium">{successMessage}</span>
          </div>
        )}

        {!selectedPlatform ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 mb-4">Select a platform to connect. You can add multiple accounts.</p>
            {platforms?.map(platform => (
              <button
                key={platform?.id}
                onClick={() => handlePlatformSelect(platform?.id)}
                className="w-full flex items-center gap-3 p-4 rounded-lg border-2 border-border hover:border-primary transition-colors"
              >
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center p-2"
                  style={{ backgroundColor: `${platform?.color}15` }}
                >
                  <Image 
                    src={platform?.logo}
                    alt={platform?.alt}
                    className="w-full h-full object-contain"
                    style={{ filter: `drop-shadow(0 0 2px ${platform?.color}40)` }}
                  />
                </div>
                <span className="text-base font-medium text-foreground">{platform?.name}</span>
              </button>
            ))}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="platform" className="block text-sm font-medium text-gray-700 mb-1">
                Platform *
              </label>
              <select
                id="platform"
                name="platform"
                value={formData?.platform}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                required
                disabled={loading}
              >
                <option value="">Select a platform</option>
                {availableOptions?.map(platform => (
                  <option key={platform?.value} value={platform?.value}>
                    {platform?.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="accountName" className="block text-sm font-medium text-gray-700 mb-1">
                Account Name *
              </label>
              <Input
                id="accountName"
                name="accountName"
                type="text"
                value={formData?.accountName}
                onChange={handleChange}
                placeholder="@username or page name"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="accountUrl" className="block text-sm font-medium text-gray-700 mb-1">
                Profile URL
              </label>
              <Input
                id="accountUrl"
                name="accountUrl"
                type="url"
                value={formData?.accountUrl}
                onChange={handleChange}
                placeholder="https://..."
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="followerCount" className="block text-sm font-medium text-gray-700 mb-1">
                Current Followers
              </label>
              <Input
                id="followerCount"
                name="followerCount"
                type="number"
                min="0"
                value={formData?.followerCount}
                onChange={handleChange}
                placeholder="0"
                disabled={loading}
              />
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                type="button"
                onClick={handleBack}
                variant="outline"
                className="flex-1"
                disabled={loading}
              >
                Back
              </Button>
              <Button
                type="submit"
                className="flex-1 flex items-center justify-center gap-2"
                disabled={loading}
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Connect
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ConnectSocialModal;